import _ from 'underscore';
import { useEffect, useState, useContext, useMemo } from 'react';
import Promise from 'bluebird';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import {
  PMAX_LITE_WIZARD_STEP_PATHS,
  ONBOARDING_PATHS,
  convertStepIdForPreviewFlow,
  getFlow,
} from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';

import { useInitializeScope } from './use-initialize-scope';
import { useInitializeCache } from './use-initialize-cache';
import { mergeInitialWizardData } from './merge-initial-wizard-data';

export function useInitializeWizard(
  stepPreference,
  adExtensionsDynamicFeaturesLoader,
  theme
) {
  const serverContext = useContext(ServerContext);
  const preference = useContext(PreferenceContext);
  const { scope } = useContext(ScopeContext);
  const instrumentation = useContext(InstrumentationContext);
  const i18n = useContext(I18nContext);
  const hasAccount = !!(serverContext && serverContext.CurrentAccount);

  // Below pilot is for general user
  const isPmaxLitePreviewFlowEnabled = usePilotsValue('IsPmaxLitePreviewFlowEnabled');
  const isPMaxChinaFlowEnabled = usePilotsValue('isPMaxChinaFlowEnabled');
  const isCollectBusinessInfoEnabled = usePilotsValue('IsPmaxLiteCollectBusinessInfoStepEnabled') && !isSDK();
  const isChinaSignup = usePilotsValue('isCNMarketExpansionEnabled');
  // autoLog:false — traffic-split exposure must be logged at CAMPAIGN_CREATION_METHOD_SELECTION, not here
  const { value: isEnglishAgenticSignupPilot } = usePilotsValue('isPMaxLiteAgenticSignupEnabled', false, true /* includeNewAccountFlights */, false /* autoLog */);
  const { value: isNonEnglishAgenticSignupPilot } = usePilotsValue('isPMaxLiteAgenticSignupNonEnglishEnabled', false, true /* includeNewAccountFlights */, false /* autoLog */);
  const isOverviewOnboarding = usePilotsValue('isOverviewOnboardingEnabled', false, true /* includeNewAccountFlights */);

  const currentLocale = (serverContext && serverContext.CurrentLocale) || '';
  const isEnglishLocale = currentLocale.toLowerCase().split('-')[0] === 'en';
  const isAgenticSignup = isEnglishLocale
    ? isEnglishAgenticSignupPilot
    : isNonEnglishAgenticSignupPilot;

  const [isInitialized, setIsInitialized] = useState(false);
  const [initialStep, setInitialStep] = useState();
  const [initialFlowFactors, setInitialFlowFactors] = useState();
  const [initialWizardData, setInitialWizardData] = useState();

  const {
    scopeInitialized,
    hasCampaign,
    hasAssetGroup,
    campaignId,
    adGroupId,
    languages,
  } = useInitializeScope({ isGoalBasedSignup: true, selectedCampaignType: stepPreference.flowFactors?.selectedCampaignType });

  const {
    cacheInitialized,
    cache,
  } = useInitializeCache();

  const abTestActivity = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'SignUp',
    activityName: 'ABTestToPreference',
  })?.activity, [instrumentation]);

  useEffect(() => {
    if (isOverviewOnboarding) {
      if (preference?.setAtAccountLevel) {
        preference?.setAtAccountLevel?.({
          Name: 'OverviewOnboarding_treatment',
          Data: true,
        });
        abTestActivity?.trace(`[PMaxLiteSignup] OverviewOnboarding_treatment set to true on AccountLevel preferences for serverContext.CurrentAccount: ${serverContext?.CurrentAccount?.Id}`);
      } else {
        abTestActivity?.trace(`[PMaxLiteSignup] OverviewOnboarding_treatment is true but preferences are not ready for serverContext.CurrentAccount: ${serverContext?.CurrentAccount?.Id}`);
      }
    }
  }, [isOverviewOnboarding, preference, abTestActivity, serverContext]);

  useEffect(() => {
    if (!isInitialized && stepPreference && scopeInitialized && cacheInitialized) {
      const { stepId: preferenceStepId } = stepPreference;
      const { flowFactors: preferenceFlowFactors } = stepPreference;

      const { isCacheFlow } = preferenceFlowFactors || {};

      // Set preview flow when previous is cache flow or preview flow pilot is enabled
      const isPreviewFlow = isCacheFlow || isPmaxLitePreviewFlowEnabled;

      const campaignCreationMethodStep = !isChinaSignup
        ? PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD_SELECTION
        : PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD;
      // set first step to collect business info if the pilot is enabled
      const firstStep = isCollectBusinessInfoEnabled && !isChinaSignup
        ? PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO
        : campaignCreationMethodStep;

      let stepId = convertStepIdForPreviewFlow(isPreviewFlow, preferenceStepId)
        || firstStep;
      let flowFactors = {
        ...preferenceFlowFactors,
        isCollectBusinessInfo: isCollectBusinessInfoEnabled,
        isPreviewFlow,
        isChinaSignup,
        isGoalBasedSignup: true,
        isAgenticSignup,
      };

      const {
        isSkipCampaign, isGoogleImport, isFacebookImport,
      } = flowFactors;
      const shouldHaveCampaignCreated = !isSkipCampaign && !isGoogleImport && !isFacebookImport
        && !_.contains(ONBOARDING_PATHS, stepId);

      const currentFlow = getFlow(flowFactors);
      const canNotTrustPreference = !_.contains(currentFlow, stepId)
        || (!hasAccount && stepId !== firstStep)
        || (!isChinaSignup && !hasCampaign && shouldHaveCampaignCreated)
        || (!isPMaxChinaFlowEnabled && stepId === PMAX_LITE_WIZARD_STEP_PATHS.CHINA_DOMESTIC);

      if (canNotTrustPreference) {
        // restart the wizard if the preference is not valid
        stepId = firstStep;
        flowFactors = {
          isPreviewFlow,
          isChinaSignup,
          isGoalBasedSignup: true,
          isAgenticSignup,
        };
      }

      if (cache && !hasAssetGroup) {
        const { website } = cache;
        // Go to a first step and show shimmer to load wizard
        stepId = firstStep;
        flowFactors = {
          isPreviewFlow: true,
          isCacheFlow: true,
          isGoalBasedSignup: true,
          isAgenticSignup,
        };
        setInitialWizardData({
          loadWizardDirectly: !isCollectBusinessInfoEnabled,
          websiteUrl: website,
          cache,
        });
      }

      setInitialStep(stepId);
      setInitialFlowFactors(flowFactors);

      if (window.isLegacyInitDone) {
        const baseWizardData = {
          // wizardData need for search campaign flow
          campaignId,
          adGroupId,
          currentLanguages: languages,
          previousLanguages: languages,
        };

        Promise.props({
          adExtensionsDynamicFeatures: adExtensionsDynamicFeaturesLoader({
            permissions: serverContext.Permissions,
            theme,
            scope,
            instrumentation,
            i18n,
          }),
          adExtensionsAssociationsFactory: import(/* webpackChunkName: "campaign-adgroup-creation" */ '@bingads-webui-campaign-react/ad-extensions-associations-factory'),
        })
          .then(({ adExtensionsDynamicFeatures, adExtensionsAssociationsFactory }) => {
            const wizardDataWithExtensions = {
              ...baseWizardData,
              adExtensionsDynamicFeatures,
              adExtensionsAssociationsFactory,
            };
            setInitialWizardData(currentWizardData => (
              mergeInitialWizardData(currentWizardData, wizardDataWithExtensions)
            ));
            console.log('Ad extensions dynamic features loaded for goal based signup wizard.');
          })
          .catch((e) => {
            // Fallback to basic wizard data if extensions loading fails
            setInitialWizardData(currentWizardData => (
              mergeInitialWizardData(currentWizardData, baseWizardData)
            ));
            console.log('Failed to load ad extensions dynamic features for goal based signup wizard. Error:', e);
          })
          .finally(() => {
            // set isInitialized to true after setting the initial values
            setIsInitialized(true);
          });
      } else {
        // set isInitialized to true after setting the initial values
        setIsInitialized(true);
      }
    }
  }, [cache, cacheInitialized, hasAccount, hasAssetGroup, hasCampaign,
    isChinaSignup, isCollectBusinessInfoEnabled, isInitialized,
    isPmaxLitePreviewFlowEnabled, scopeInitialized, stepPreference,
    isPMaxChinaFlowEnabled, isAgenticSignup, campaignId, adGroupId, languages, serverContext,
    currentLocale, adExtensionsDynamicFeaturesLoader, theme, scope, instrumentation, i18n]);

  return {
    isInitialized,
    initialStep,
    initialFlowFactors,
    initialWizardData,
  };
}
